import {
  QueryFactory,
  TransportClientDto,
  TransportProtocol,
} from '@lmt/shared-types';
import { AppTooltip } from '@lmt/unicorn-shared/app/components/common/ui-kit/tooltip/AppTooltip';
import ClockIcon from '@lmt/unicorn-shared/app/icons/clock.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useEffect, useMemo, useState } from 'react';

import styles from './TransporterState.module.scss';
import { subMonths, subSeconds } from 'date-fns';
import { getDistanceToNow } from '@lmt/unicorn-shared/app/helpers/date-helper';

interface Props {
  protocol: TransportProtocol;
}

export const TransporterState: React.FC<Props> = React.memo(
  function TransporterState(props) {
    const { protocol } = props;
    const i18n = useScopedTranslation('gdt.messages.transporter_state');
    const gdtClientQuery = QueryFactory.TransportClientQuery.useGetQuery(
      protocol,
      {
        refetchInterval: 10000,
      },
    );

    // Refresh transport connector every 20 seconds
    const [time, setTime] = useState(Date.now());

    useEffect(() => {
      const interval = setInterval(() => setTime(Date.now()), 20000);
      return () => {
        clearInterval(interval);
      };
    }, [gdtClientQuery.data]);

    const onlineClientsCount = useMemo(() => {
      if (!gdtClientQuery.data) {
        return 0;
      }
      const newDate = new Date();
      const onlineLimit = subSeconds(newDate, 20);
      const onlineClientsCount = gdtClientQuery.data.filter(
        (x) => x.lastActivityDateTime > onlineLimit,
      ).length;
      return onlineClientsCount;
    }, [gdtClientQuery.data, time]);

    const isConnected = useMemo(() => {
      return onlineClientsCount > 0;
    }, [onlineClientsCount]);

    const recentlyActiveClients = useMemo<TransportClientDto[]>(() => {
      if (!gdtClientQuery.data) {
        return [];
      }
      const newDate = new Date();
      const recentlyActiveLimit = subMonths(newDate, 1);
      const recentlyActiveClients = gdtClientQuery.data.filter(
        (x) => x.lastActivityDateTime > recentlyActiveLimit,
      );

      return recentlyActiveClients;
    }, [gdtClientQuery.data, time]);

    const tooltip = useMemo(() => {
      if (recentlyActiveClients.length === 0) {
        return i18n.t('empty');
      }
      return (
        <div>
          {recentlyActiveClients.map((x) => {
            const info = i18n.t('tooltip', {
              name: x.name,
              date: getDistanceToNow(x.lastActivityDateTime),
            });
            return <div>{info}</div>;
          })}
        </div>
      );
    }, [recentlyActiveClients]);

    return (
      <div className={styles.container}>
        <div data-connected={isConnected} className={styles.ellipse}></div>
        <div className={styles.text} data-connected={isConnected}>
          {i18n.t('state', { count: onlineClientsCount })}
        </div>
        {!!tooltip && (
          <AppTooltip title={tooltip}>
            <ClockIcon className={styles.clockIcon} />
          </AppTooltip>
        )}
      </div>
    );
  },
);
